/* eslint-disable import-extensions/file-extension-required */
import { MediaClient } from '@atlaskit/media-client/media-client';
import type { FileState } from '@atlaskit/media-client/media-state';
import { globalMediaEventEmitter } from '@atlaskit/media-client/global-media-event-emitter';
import type { MediaProvider as BaseMediaProvider } from '@atlaskit/editor-common/provider-factory/media-provider';
import {
	fetchTokenForSourceProduct,
	requiresCrossProductAuth,
} from '@atlaskit/editor-synced-block-provider/tokenRouting';
import type {
	AuthContext,
	MediaClientConfig,
	AuthProvider as MediaAuthProvider,
} from '@atlaskit/media-core/auth';
import type { UploadRejectionData } from '@atlaskit/media-picker/types';

import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { fetchSnippetMediaToken } from './fetchSnippetMediaToken.ts';
import { SnippetScope } from '../snippetScope.ts';

import { cacheWithExp } from '../cacheWithExp.ts';
// eslint-disable-next-line import/no-cycle
import {
	getTemplateMediaContextValues,
	getTemplateMediaAuth,
	// oxlint-disable-next-line import/no-cycle
} from '../TemplateMediaProvider/index.ts';

import type { graphqlHelpersFetchMediaTokenQuery$data } from './__generated__/graphqlHelpersFetchMediaTokenQuery.graphql';

type MediaDownloadTokenQuery_content_nodes = NonNullable<
	NonNullable<NonNullable<graphqlHelpersFetchMediaTokenQuery$data['content']>['nodes']>[number]
>;
type MediaDownloadTokenQuery_mediaConfiguration = NonNullable<
	graphqlHelpersFetchMediaTokenQuery$data['mediaConfiguration']
>;
import {
	fetchMediaToken,
	fetchExternalShareMediaToken,
	getUploadToken,
	getIsExportEnabledForContent,
	getCommentUploadToken,
	fetchExternalContentMediaSessionToken,
} from './graphqlHelpers.ts';
import { attachUploadedMedia } from './media-helpers.ts';

/**
 * Extended MediaProvider that adds refreshUploadDataIfNeeded for providers that
 * keep a single instance alive across SPA navigations. Once refreshUploadDataIfNeeded
 * is added to the base type in @atlaskit/editor-common, this local extension can be removed.
 */
type MediaProvider = BaseMediaProvider & {
	refreshUploadDataIfNeeded?: () => Promise<void>;
	renameFile?: (fileId: string, name: string) => Promise<void>;
};

type ConfigOptions = {
	contentId: string;
	contentAri?: string;
	contentProduct?: 'confluence-page' | 'jira-work-item';
	spaceKey?: string | null;
	mediaDspContentId?: string;
	isExternalShare: boolean;
	mediaToken?: string;
	/** Enables Confluence `contentId-<id>` collection routing for view-media token requests. */
	resolveContentIdFromCollection?: boolean;
	shareToken?: string;
};

type EditorMediaProviderOptions = {
	contentType: string;
	cloudId?: string;
	onUploadRejection?: (data: UploadRejectionData) => boolean;
} & ConfigOptions;

export type TokenData = {
	config: ConfigData;
	token: string;
	collectionId?: string;
};

export type ConfigData = MediaDownloadTokenQuery_mediaConfiguration;

const DEFAULT_CONFIG: ConfigData = { clientId: '', fileStoreUrl: '' };

export type AuthProvider = {
	clientId: string;
	baseUrl: string;
	token: string;
};

const cachedGetDownloadData = cacheWithExp<TokenData>(
	async ({
		contentId,
		contentAri,
		contentProduct,
		isExternalShare,
		shareToken,
	}: Pick<
		ConfigOptions,
		'contentId' | 'contentAri' | 'contentProduct' | 'isExternalShare' | 'shareToken'
	>) => {
		if (
			requiresCrossProductAuth({
				hostProduct: 'confluence-page',
				sourceProduct: contentProduct,
			})
		) {
			return fetchTokenForSourceProduct({
				contentAri: contentAri ?? contentId,
				contentId,
				sourceProduct: contentProduct,
			});
		}

		if (isExternalShare && shareToken) {
			const tokenResponse = await fetchExternalContentMediaSessionToken(shareToken);
			const tokenData = tokenResponse?.data;
			return {
				token: tokenData?.externalContentMediaSession?.accessTokens?.readOnlyToken?.value || '',
				config: tokenData?.externalContentMediaSession?.configuration || DEFAULT_CONFIG,
			};
		}

		// Fallback to existing logic for backward compatibility
		const response = await (isExternalShare ? fetchExternalShareMediaToken : fetchMediaToken)(
			contentId,
		);
		const data = response?.data;
		const externalData = data?.['singleContent'];
		const content: MediaDownloadTokenQuery_content_nodes = isExternalShare
			? externalData
			: data?.['content']?.nodes?.[0];
		return {
			token: content?.body?.atlas_doc_format?.mediaToken?.token || '',
			config: data?.mediaConfiguration || DEFAULT_CONFIG,
		};
	},
	(json) => json.token,
);

type AuthProviderDataGetter = (authContext?: AuthContext) => Promise<TokenData>;

const getContentIdFromCollection = (authContext?: AuthContext): string | undefined => {
	const match = /^contentId-(\d+)$/.exec(authContext?.collectionName ?? '');
	return match?.[1];
};

export const createAuthProvider =
	(dataGetter: AuthProviderDataGetter): MediaAuthProvider =>
	async (authContext) => {
		const data = await dataGetter(authContext);
		return {
			token: data?.token || '',
			clientId: data?.config?.clientId || '',
			baseUrl: data?.config?.fileStoreUrl || '',
		};
	};

/**
 * Creates a Media Auth provider that can be used for both view and upload.
 * The specific token is created/supplied based on the AuthContext parameters.
 * Currently, the upload token is only supplied for file and collection `update` action, used for managing Video Captions.
 * Media should integrate all the other operations before we can remove viewMediaClientConfig and uploadMediaClientConfig in favour of this one.
 * */
export const createViewAndUploadAuthProvider =
	(
		viewDataGetter: AuthProviderDataGetter,
		uploadDataGetter: AuthProviderDataGetter,
	): MediaAuthProvider =>
	async (params) => {
		// Currently, supplying upload token only for file and collection update action.
		// If no params are provided, the view token is returned.
		const isUpdateAction = !!params?.access?.find(
			({ type, actions }) =>
				(type === 'file' || type == 'collection') && actions.includes('update'),
		);

		const data = await (isUpdateAction ? uploadDataGetter() : viewDataGetter(params));
		return {
			token: data?.token || '',
			clientId: data?.config?.clientId || '',
			baseUrl: data?.config?.fileStoreUrl || '',
		};
	};

/**
 * Returns snippet context when contextId parses to { snippetId: string; scope?: string }.
 * Used to detect media nodes from snippets so we can fetch snippet media token.
 */
export const getSnippetContextId = (
	contextId: string,
): { snippetId: string; scope?: string } | undefined => {
	try {
		const { snippetId, scope } = JSON.parse(contextId);
		return snippetId ? { snippetId, scope } : undefined;
	} catch {} // not JSON or not a SnippetContextId.
};

export const getAuthFromContext = async (
	contextId: string,
	contentProduct?: ConfigOptions['contentProduct'],
	contentAri?: string,
): Promise<AuthProvider> => {
	if (
		requiresCrossProductAuth({
			hostProduct: 'confluence-page',
			sourceProduct: contentProduct,
		})
	) {
		const auth = await fetchTokenForSourceProduct({
			contentAri: contentAri ?? contextId,
			contentId: contextId,
			sourceProduct: contentProduct,
		});
		return {
			clientId: auth.config.clientId || '',
			token: auth.token || '',
			baseUrl: auth.config.fileStoreUrl || '',
		};
	}

	if (expVal('platform_editor_blocks_conf', 'isEnabled', false)) {
		const snippetContext = getSnippetContextId(contextId);
		if (snippetContext && snippetContext.scope !== SnippetScope.GLOBAL) {
			const auth = await fetchSnippetMediaToken(snippetContext.snippetId);
			return { clientId: auth.clientId, token: auth.token, baseUrl: auth.baseUrl };
		}
	}

	const { collectionId, templateId, spaceKey } = getTemplateMediaContextValues(contextId);

	// template media informtion is stored slightly different in the context
	// to be able to discern which call to make
	if (collectionId || templateId) {
		return getTemplateMediaAuth({ collectionId, spaceKey, templateId });
	}

	const response = await fetchMediaToken(contextId);
	const { clientId = '', fileStoreUrl = '' } = response?.data?.mediaConfiguration || {};

	return {
		clientId,
		token: response?.data?.content?.nodes?.[0]?.body?.atlas_doc_format?.mediaToken?.token || '',
		baseUrl: fileStoreUrl || '',
	};
};

const cachedGetUploadData = cacheWithExp<TokenData>(
	async (options: EditorMediaProviderOptions) => {
		const response =
			options.contentType === 'comment' && options.cloudId
				? await getCommentUploadToken(options.contentId, options.cloudId)
				: await getUploadToken(options.contentId);
		const session = response?.data?.contentMediaSession;
		return {
			token: session?.token?.value || '',
			config: session?.configuration || DEFAULT_CONFIG,
			collectionId: session?.collection || '',
		};
	},
	(json) => json.token,
);

const getUploadCollectionId = async (options: ConfigOptions) => {
	const uploadData = await cachedGetUploadData.getValue(options);
	return uploadData.collectionId;
};

const createUploadAuthProviderDataGetter = (options: EditorMediaProviderOptions) => () =>
	cachedGetUploadData.getValue(options);

const getUploadMediaClientConfig = async (
	options: EditorMediaProviderOptions,
): Promise<MediaClientConfig> => ({
	authProvider: createAuthProvider(createUploadAuthProviderDataGetter(options)),
	getAuthFromContext,
	useSha256ForUploads: true,
});

const createRenameFile = (
	mediaClientConfig: MediaClientConfig,
	getCollection: () => string | undefined,
): NonNullable<MediaProvider['renameFile']> => {
	const mediaClient = new MediaClient(mediaClientConfig);

	return async (fileId, name) => {
		const collection = getCollection();
		if (!collection) {
			throw new Error('Cannot rename media file without a collection');
		}

		await mediaClient.mediaStore.request('/files/rename', {
			method: 'PUT',
			endpoint: '/files/rename',
			authContext: { collectionName: collection },
			params: { collection },
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				collection,
				files: [{ fileId, fileName: name, oldFileName: '' }],
			}),
		});
	};
};

const cachedGetExportDspData = cacheWithExp<{
	isExportAllowed: boolean | undefined | null;
	exp: number;
}>(
	async ({ contentId, spaceKey }: { contentId: string; spaceKey?: string }) => {
		const response = await getIsExportEnabledForContent(contentId, spaceKey);
		const isExportAllowed = response?.data?.dataSecurityPolicy?.isActionEnabledForContent?.allowed;
		return {
			isExportAllowed,
			exp: Date.now() + 1000 * 60, // 1 minute
		};
	},
	(json) => json.exp,
);

const getEnforceDataSecurityPolicy = async (options: ConfigOptions): Promise<boolean> => {
	if (!fg('dlp_attachment_download_respects_data_export_dsp')) return false;
	const { contentId, spaceKey, mediaDspContentId } = options;
	try {
		const relevantContentId = mediaDspContentId || contentId;
		const response = await cachedGetExportDspData.getValue({
			contentId: relevantContentId,
			spaceKey,
		});
		const isExportAllowed = response?.isExportAllowed;
		return !isExportAllowed;
	} catch {
		// Fails CLOSED
		return true;
	}
};

const createViewAuthProviderDataGetter =
	(options: ConfigOptions) => async (authContext?: AuthContext) => {
		const contentIdFromCollection =
			options.resolveContentIdFromCollection &&
			!options.isExternalShare &&
			!options.mediaToken &&
			options.contentProduct !== 'jira-work-item' &&
			expValEquals('platform_media_token_nested_macro', 'isEnabled', true)
				? getContentIdFromCollection(authContext)
				: undefined;
		const contentId = contentIdFromCollection ?? options.contentId;

		// Only pass contentId, isExternalShare, and shareToken to the cacheWithExp function
		// to avoid unnecessary re-fetching of the token if other options differ
		const authQueryOptions = {
			contentId,
			contentAri: options.contentAri,
			contentProduct: options.contentProduct,
			isExternalShare: options.isExternalShare,
			shareToken: options.shareToken,
		};
		let data: TokenData = await cachedGetDownloadData.getValue(authQueryOptions);

		if (options.mediaToken) {
			data = {
				...data,
				token: options.mediaToken,
			};
		}

		// For drafts mediaToken by default is null and created once any media added to them
		// So, once publishing draft that just got media but token was cached before as `null`
		// we need to re-fetch the token without the cache
		if (!data.token) {
			data = await cachedGetDownloadData.forceGetValue(authQueryOptions);
		}

		return data;
	};

export const getViewMediaClientConfig = async (
	options: ConfigOptions,
): Promise<MediaClientConfig> => {
	return {
		enforceDataSecurityPolicy: await getEnforceDataSecurityPolicy(options),
		authProvider: createAuthProvider(createViewAuthProviderDataGetter(options)),
		getAuthFromContext: (contextId: string) =>
			getAuthFromContext(contextId, options.contentProduct, options.contentAri),
	};
};

export const getViewAndUploadMediaClientConfig = async (
	options: EditorMediaProviderOptions,
): Promise<MediaClientConfig> => ({
	enforceDataSecurityPolicy: await getEnforceDataSecurityPolicy(options),
	authProvider: createViewAndUploadAuthProvider(
		createViewAuthProviderDataGetter(options),
		createUploadAuthProviderDataGetter(options),
	),
	getAuthFromContext: (contextId: string) =>
		getAuthFromContext(contextId, options.contentProduct, options.contentAri),
});

export const createRendererMediaProvider = async (
	options: ConfigOptions,
): Promise<MediaProvider> => ({
	viewMediaClientConfig: await getViewMediaClientConfig(options),
});

export const createEditorMediaProvider = async (
	options: EditorMediaProviderOptions,
): Promise<MediaProvider> => {
	registerGlobalFileAddedListener(options);

	const [
		viewMediaClientConfig,
		uploadMediaClientConfig,
		viewAndUploadMediaClientConfig,
		collection,
	] = await Promise.all([
		getViewMediaClientConfig(options),
		getUploadMediaClientConfig(options),
		fg('platform_media_video_captions') ? getViewAndUploadMediaClientConfig(options) : undefined,
		getUploadCollectionId(options),
	]);

	return {
		viewMediaClientConfig,
		uploadParams: {
			collection,
			onUploadRejection: options.onUploadRejection,
		},
		uploadMediaClientConfig,
		viewAndUploadMediaClientConfig,
		renameFile: createRenameFile(uploadMediaClientConfig, () => collection),
	};
};

// Control Data export while in PDF Export page
export const controlDataExportOnCertainFeatures = (pathname: string): boolean => {
	return !!PDF_EXPORT_PAGE.match(pathname);
};

let previousAttachUploadedMediaListener: (fileState: FileState) => void;

const registerGlobalFileAddedListener = (options: EditorMediaProviderOptions) => {
	if (previousAttachUploadedMediaListener) {
		globalMediaEventEmitter.off('file-added', previousAttachUploadedMediaListener);
	}
	previousAttachUploadedMediaListener = attachUploadedMedia(options);
	globalMediaEventEmitter.on('file-added', previousAttachUploadedMediaListener);
};

/**
 * Dynamic (getter-based) variants ---------------------------------------------------------------
 * These allow creating auth providers that always read the latest "options" instead of
 * closing over the snapshot present at creation time. Useful when you want to keep a single
 * MediaProvider instance stable across navigation (e.g. contentId changes) while ensuring
 * new token fetches use fresh parameters.
 *
 * The existing exported helpers (createEditorMediaProvider, createRendererMediaProvider, etc.)
 * intentionally snapshot their options. Keep those for current behaviour. Use the *Dynamic
 * variants below when you suspect stale closures.
 */

type OptionsGetter = () => ConfigOptions;
type EditorOptionsGetter = () => EditorMediaProviderOptions;

const createViewAuthProviderDataGetterDynamic =
	(getOptions: OptionsGetter) => async (authContext?: AuthContext) => {
		const options = getOptions();
		return createViewAuthProviderDataGetter(options)(authContext);
	};

const createUploadAuthProviderDataGetterDynamic = (getOptions: EditorOptionsGetter) => async () => {
	const options = getOptions();
	return createUploadAuthProviderDataGetter(options)();
};

export const getViewMediaClientConfigDynamic = async (
	getOptions: OptionsGetter,
): Promise<MediaClientConfig> => {
	const options = getOptions();
	return {
		enforceDataSecurityPolicy: await getEnforceDataSecurityPolicy(options),
		authProvider: createAuthProvider(createViewAuthProviderDataGetterDynamic(getOptions)),
		getAuthFromContext: (contextId: string) =>
			getAuthFromContext(contextId, getOptions().contentProduct, getOptions().contentAri),
	};
};

export const getViewAndUploadMediaClientConfigDynamic = async (
	getOptions: EditorOptionsGetter,
): Promise<MediaClientConfig> => {
	const options = getOptions();
	return {
		enforceDataSecurityPolicy: await getEnforceDataSecurityPolicy(options),
		authProvider: createViewAndUploadAuthProvider(
			createViewAuthProviderDataGetterDynamic(getOptions),
			createUploadAuthProviderDataGetterDynamic(getOptions),
		),
		getAuthFromContext: (contextId: string) =>
			getAuthFromContext(contextId, getOptions().contentProduct, getOptions().contentAri),
	};
};

export const createRendererMediaProviderDynamic = async (
	getOptions: OptionsGetter,
): Promise<MediaProvider> => ({
	viewMediaClientConfig: await getViewMediaClientConfigDynamic(getOptions),
});

export const createEditorMediaProviderDynamic = async (
	getOptions: EditorOptionsGetter,
): Promise<MediaProvider> => {
	const initialOptions = getOptions();
	registerGlobalFileAddedListener(initialOptions);

	// Register the global file-added listener dynamically so that SPA navigations always
	// register the attachment callback against the current page's contentId.
	const registerDynamicFileAddedListener = () => {
		registerGlobalFileAddedListener(getOptions());
	};

	// Track the last contentId we fetched upload data for, and the cached result.
	// This lets us detect a contentId change and eagerly re-fetch before any upload.
	let lastFetchedContentId = initialOptions.contentId;
	let latestUploadData: TokenData;

	// Deduplicate concurrent calls to refreshUploadDataIfNeeded — if a refresh is already
	// in-flight (e.g. from useMediaOptions and authProvider both calling simultaneously),
	// subsequent callers share the same promise rather than racing to write latestUploadData.
	let inFlightRefresh: Promise<void> | undefined;

	// Refresh upload data for the current options if contentId has changed since the
	// last fetch. Concurrent calls are deduplicated via inFlightRefresh.
	// Called by useMediaOptions on navigation so all uploadParams.collection read sites
	// get the correct collection without needing to do anything special themselves.
	const refreshUploadDataIfNeeded = async (): Promise<void> => {
		const currentOptions = getOptions();
		if (currentOptions.contentId !== lastFetchedContentId) {
			if (inFlightRefresh) {
				return inFlightRefresh;
			}
			inFlightRefresh = (async () => {
				registerDynamicFileAddedListener();
				latestUploadData = await cachedGetUploadData.getValue(currentOptions);
				lastFetchedContentId = currentOptions.contentId;
				inFlightRefresh = undefined;
			})();
			return inFlightRefresh;
		}
	};

	// Build a fully-dynamic uploadMediaClientConfig whose authProvider always fetches
	// upload data for the CURRENT options so tokens stay correct across SPA navigations.
	// Note: we do NOT call registerDynamicFileAddedListener here — the listener is only
	// re-registered in refreshUploadDataIfNeeded (on actual navigation), not on every
	// token fetch, to avoid unnecessary teardown/rebuild of the module-level singleton.
	const dynamicUploadMediaClientConfig: MediaClientConfig = {
		authProvider: createAuthProvider(async () => {
			const currentOptions = getOptions();
			const uploadData = await cachedGetUploadData.getValue(currentOptions);
			latestUploadData = uploadData;
			lastFetchedContentId = currentOptions.contentId;
			return uploadData;
		}),
		getAuthFromContext,
		useSha256ForUploads: true,
	};

	// Fetch all async dependencies in parallel — no waterfall.
	const [viewMediaClientConfig, viewAndUploadMediaClientConfig, initialUploadData] =
		await Promise.all([
			getViewMediaClientConfigDynamic(() => getOptions()),
			fg('platform_media_video_captions')
				? getViewAndUploadMediaClientConfigDynamic(() => getOptions())
				: undefined,
			cachedGetUploadData.getValue(getOptions()),
		]);
	latestUploadData = initialUploadData;

	return {
		viewMediaClientConfig,
		// uploadParams is resolved via a getter so SPA navigations don't leave the collection
		// frozen to the page that was active when this provider was first created.
		get uploadParams() {
			return {
				collection: latestUploadData.collectionId,
				onUploadRejection: getOptions().onUploadRejection,
			};
		},
		refreshUploadDataIfNeeded,
		uploadMediaClientConfig: dynamicUploadMediaClientConfig,
		viewAndUploadMediaClientConfig,
		renameFile: createRenameFile(
			dynamicUploadMediaClientConfig,
			() => latestUploadData.collectionId,
		),
	};
};

/**
 * Usage sketch (React):
 * const liveOptionsRef = useRef(currentOptions);
 * liveOptionsRef.current = currentOptions; // update on changes
 * const providerPromise = useMemo(() => createEditorMediaProviderDynamic(() => liveOptionsRef.current), []);
 * editorApi.media.actions.setProvider(providerPromise);
 */
